import manifest from '@/images/headers/manifest.json';

interface BannerManifest {
  aliases?: Record<string, string>;
  default: string[];
  subjects: Record<string, string[]>;
}

export interface CourseForBannerAssignment {
  creationIndex?: number;
  id: string;
  parentSubject: string | null | undefined;
}

const DEFAULT_SUBJECT_KEY = '__default__';
const typedManifest = manifest as BannerManifest;

const imageModules = import.meta.glob<{ default: string }>(
  '../../images/headers/*.{jpg,jpeg,png,webp,svg}',
  { eager: true },
);

const imageMap = new Map<string, string>();
const basenameImageMap = new Map<string, string>();

for (const [path, module] of Object.entries(imageModules)) {
  const filename = path.split('/').pop()?.toLowerCase() ?? '';

  if (!filename) {
    continue;
  }

  imageMap.set(filename, module.default);

  const extensionIndex = filename.lastIndexOf('.');
  const basename =
    extensionIndex >= 0 ? filename.slice(0, extensionIndex) : filename;

  if (!basenameImageMap.has(basename)) {
    basenameImageMap.set(basename, module.default);
  }
}

function normalizeSubject(subject: string | null | undefined) {
  return (subject ?? '').trim().toLowerCase();
}

function resolveSubjectKey(subject: string | null | undefined) {
  const normalizedSubject = normalizeSubject(subject);
  const aliasedSubject = typedManifest.aliases?.[normalizedSubject];

  if (aliasedSubject && typedManifest.subjects?.[aliasedSubject]) {
    return aliasedSubject;
  }

  if (typedManifest.subjects?.[normalizedSubject]) {
    return normalizedSubject;
  }

  return DEFAULT_SUBJECT_KEY;
}

function resolveAssetUrl(filename: string) {
  const normalizedFilename = filename.trim().toLowerCase();

  if (!normalizedFilename) {
    return null;
  }

  const directMatch = imageMap.get(normalizedFilename);

  if (directMatch) {
    return directMatch;
  }

  const extensionIndex = normalizedFilename.lastIndexOf('.');
  const basename =
    extensionIndex >= 0
      ? normalizedFilename.slice(0, extensionIndex)
      : normalizedFilename;

  return basenameImageMap.get(basename) ?? null;
}

function getAvailableImagePool(filenames: string[] | undefined) {
  return (filenames ?? []).filter(
    (filename) => resolveAssetUrl(filename) !== null,
  );
}

function getImagePoolForSubjectKey(subjectKey: string) {
  if (subjectKey === DEFAULT_SUBJECT_KEY) {
    return getAvailableImagePool(typedManifest.default);
  }

  const subjectImages = getAvailableImagePool(
    typedManifest.subjects?.[subjectKey],
  );

  return subjectImages.length > 0
    ? subjectImages
    : getAvailableImagePool(typedManifest.default);
}

function compareCoursesForBannerAssignment(
  first: CourseForBannerAssignment,
  second: CourseForBannerAssignment,
) {
  const firstHasCreationIndex =
    first.creationIndex !== undefined && Number.isFinite(first.creationIndex);
  const secondHasCreationIndex =
    second.creationIndex !== undefined && Number.isFinite(second.creationIndex);

  if (firstHasCreationIndex && secondHasCreationIndex) {
    const creationIndexDifference =
      (first.creationIndex ?? 0) - (second.creationIndex ?? 0);

    if (creationIndexDifference !== 0) {
      return creationIndexDifference;
    }
  } else if (firstHasCreationIndex) {
    return -1;
  } else if (secondHasCreationIndex) {
    return 1;
  }

  return first.id.localeCompare(second.id);
}

/**
 * Simple string hash that produces a stable positive integer.
 * Used to deterministically assign a banner image to a course.
 */
export function stableHash(str: string): number {
  let hash = 0;

  for (let index = 0; index < str.length; index += 1) {
    const character = str.charCodeAt(index);
    hash = ((hash << 5) - hash + character) | 0;
  }

  return Math.abs(hash);
}

/**
 * Assigns banner image filenames for a full course list.
 *
 * Courses are grouped by their resolved subject, sorted deterministically
 * within each subject, then dealt subject images round-robin. This prevents
 * duplicate same-subject images until that subject's pool has been exhausted.
 */
export function assignCourseBannerImages(
  courses: CourseForBannerAssignment[],
): Map<string, string> {
  const groupedBySubject = new Map<string, CourseForBannerAssignment[]>();

  for (const course of courses) {
    const subjectKey = resolveSubjectKey(course.parentSubject);
    const subjectCourses = groupedBySubject.get(subjectKey) ?? [];
    subjectCourses.push(course);
    groupedBySubject.set(subjectKey, subjectCourses);
  }

  const assignments = new Map<string, string>();

  for (const [subjectKey, subjectCourses] of groupedBySubject) {
    const images = getImagePoolForSubjectKey(subjectKey);

    if (images.length === 0) {
      continue;
    }

    [...subjectCourses]
      .sort(compareCoursesForBannerAssignment)
      .forEach((course, indexInSubject) => {
        assignments.set(
          course.id,
          images[indexInSubject % images.length] ?? '',
        );
      });
  }

  return assignments;
}

/**
 * Returns the manifest filename for a course banner image.
 *
 * @deprecated Use assignCourseBannerImages for list contexts to avoid
 * duplicate images within the same subject. This per-course function may
 * return duplicate images when called for multiple courses with the same
 * subject.
 *
 * Selection logic:
 * 1. Normalize the parent subject to lowercase
 * 2. Check aliases for a canonical subject name
 * 3. Look up images for that subject
 * 4. Fall back to the default image pool
 * 5. Pick an image deterministically using a hash of the course ID
 */
export function getCourseBannerImage(
  parentSubject: string | null | undefined,
  courseId: string,
): string {
  const images = getImagePoolForSubjectKey(resolveSubjectKey(parentSubject));

  if (images.length === 0) {
    return '';
  }

  const index = stableHash(courseId) % images.length;
  return images[index] ?? '';
}

export function getBannerUrlForFilename(
  filename: string | null | undefined,
): string | null {
  if (!filename) {
    return null;
  }

  return resolveAssetUrl(filename);
}

export function getCourseBannerUrl(
  parentSubject: string | null | undefined,
  courseId: string,
): string | null {
  const filename = getCourseBannerImage(parentSubject, courseId);

  return getBannerUrlForFilename(filename);
}
